import { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { Search, ArrowLeft, Save, RefreshCw, Filter } from "lucide-react";

const BASE_URL = "https://nissi-india.com";

function safeStr(v) {
  return v == null ? "" : String(v);
}

function getFirstValue(val) {
  if (Array.isArray(val)) {
    const found = val.find((x) => x != null && x !== "");
    return found !== undefined ? found : val[0];
  }
  return val;
}

function getEmployeeId(r) {
  const raw = getFirstValue(r?.fld_Employeeid ?? r?.fld_employeeid ?? r?.EmployeeId);
  return Number(raw) || 0;
}

function getBranchId(r) {
  const raw = getFirstValue(r?.fld_branchid ?? r?.fld_Branchid);
  return Number(raw) || 0;
}

function getDesignationId(r) {
  const raw = getFirstValue(r?.fld_desigid);
  return Number(raw) || 0;
}

function getLeaveOb(r) {
  const raw = getFirstValue(r?.fld_totleaveob);
  return Number(raw) || 0;
}

function pickBranchId(row) {
  return getFirstValue(row?.fld_Branchid ?? row?.branchId) ?? "";
}
function pickBranchName(row) {
  return getFirstValue(row?.fld_BranchName ?? row?.branchName) ?? "";
}
function pickDeptId(row) {
  return getFirstValue(row?.fld_deptid ?? row?.deptId) ?? "";
}
function pickDeptName(row) {
  return getFirstValue(row?.fld_departmentname ?? row?.deptName) ?? "";
}

export default function MonthlyAttendanceEntry() {
  const nav = useNavigate();
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";

  const pageBg = isDark ? "bg-[#0b1220] text-white" : "bg-[#f6f7fb] text-gray-900";
  const cardBase = isDark
    ? "bg-white/5 border border-white/10 rounded-3xl shadow-xl"
    : "bg-white border border-gray-200 rounded-3xl shadow-xl";

  const labelBase = isDark ? "text-gray-200 font-bold text-sm" : "text-gray-700 font-bold text-sm";
  const inputBase =
    "px-4 py-3 rounded-2xl outline-none border text-sm font-semibold " +
    (isDark
      ? "bg-white/5 border-white/10 text-white placeholder:text-gray-400"
      : "bg-white border-gray-200 text-gray-900 placeholder:text-gray-400");

  const btnBase =
    "inline-flex items-center justify-center gap-2 rounded-2xl px-5 py-3 font-extrabold text-sm transition active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed";

  const primaryBtn = btnBase + " " + (isDark ? "bg-emerald-500 hover:bg-emerald-400 text-white" : "bg-emerald-600 hover:bg-emerald-700 text-white");
  const grayBtn = btnBase + " " + (isDark ? "bg-white/10 hover:bg-white/15 text-white" : "bg-gray-100 hover:bg-gray-200 text-gray-900");
  const successBtn = btnBase + " " + (isDark ? "bg-blue-600 hover:bg-blue-500 text-white" : "bg-blue-600 hover:bg-blue-700 text-white");

  const finYearId = safeStr(localStorage.getItem("finYearId") || "1014");
  const loginUserId = safeStr(localStorage.getItem("userId") || localStorage.getItem("loginUserId") || "37");
  const sessionBranchId = safeStr(localStorage.getItem("branchId") || "");
  const branchType = safeStr(localStorage.getItem("branchType") || "HO");

  const axiosCfg = useMemo(
    () => ({
      baseURL: BASE_URL,
      headers: {
        "x-loginuserid": loginUserId,
        "x-branchid": sessionBranchId,
        "x-branchtype": branchType,
      },
    }),
    [loginUserId, sessionBranchId, branchType]
  );

  const [isEditMode, setIsEditMode] = useState(false);
  const [maid, setMaid] = useState(null);

  const [metaLoading, setMetaLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [months, setMonths] = useState([]);
  const [years, setYears] = useState(["Select"]);

  const [branchId, setBranchId] = useState("Select");
  const [fromDateISO, setFromDateISO] = useState("");
  const [toDateISO, setToDateISO] = useState("");
  const [monthVal, setMonthVal] = useState("Select");
  const [year, setYear] = useState("Select");
  const [departmentId, setDepartmentId] = useState("Select");

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [selectedMap, setSelectedMap] = useState({});
  const [editValues, setEditValues] = useState({}); // editable fields in edit mode

  const selectedIds = useMemo(() => Object.keys(selectedMap).filter((k) => selectedMap[k]), [selectedMap]);

  const loadMeta = async () => {
    try {
      setMetaLoading(true);
      const res = await axios.get(`/api/payroll/monthly-attendance/meta`, {
        ...axiosCfg,
        params: { finYearId },
      });
      if (!res.data?.success) throw new Error(res.data?.message || "Meta failed");

      const m = res.data;
      setBranches(m.branches || []);
      setDepartments(m.departments || []);
      setMonths(m.months || []);
      setYears(m.years || ["Select"]);

      setBranchId(m.defaultBranchId || "Select");
      setMonthVal(m.defaultMonth || "Select");
      setYear(m.defaultYear || "Select");
      setDepartmentId("Select");
      setFromDateISO("");
      setToDateISO("");
    } catch (e) {
      toast.error(e?.message || "Failed to load meta");
    } finally {
      setMetaLoading(false);
    }
  };

  useEffect(() => {
    const storedMaid = localStorage.getItem("MAID");
    if (storedMaid) {
      setIsEditMode(true);
      setMaid(storedMaid);
      loadEditData(storedMaid);
    } else {
      loadMeta();
    }
  }, []);

  const loadEditData = async (maidValue) => {
    try {
      setLoading(true);
      const res = await axios.get(`/api/payroll/monthly-attendance/edit`, {
        ...axiosCfg,
        params: { maid: maidValue, finYearId },
      });
      if (!res.data?.success) throw new Error(res.data?.message || "Failed to load edit data");

      const data = res.data.rows || [];
      setRows(data);

      // initialize editable values
      const initial = {};
      data.forEach((r) => {
        const id = getEmployeeId(r);
        if (id) {
          initial[id] = {
            daysWorked: String(r?.fld_Daysworked ?? "0"),
            leave: String(r?.fld_Leave ?? "0"),
            manageLeave: String(r?.fld_ManageLeave ?? "0"),
            holiday: String(r?.fld_holiday ?? "0"),
            absence: String(r?.fld_Absence ?? "0"),
            ot: String(r?.fld_OT ?? "0"),
            remarks: String(r?.fld_Remarks ?? ""),
          };
        }
      });
      setEditValues(initial);
    } catch (e) {
      toast.error(e?.message || "Failed to load edit record");
    } finally {
      setLoading(false);
    }
  };

  const onSearch = async () => {
    if (!branchId || branchId === "Select") return toast.error("Select Branch");
    if (!monthVal || monthVal === "Select") return toast.error("Select Month");
    if (!year || year === "Select") return toast.error("Select Year");

    try {
      setLoading(true);
      setSelectedMap({});

      const payload = {
        branchId: safeStr(branchId),
        monthVal: safeStr(monthVal),
        year: safeStr(year),
        departmentId: safeStr(departmentId || "Select"),
        fromDate: fromDateISO,
        toDate: toDateISO,
      };

      const res = await axios.post(`/api/payroll/monthly-attendance/search`, payload, {
        ...axiosCfg,
        params: { finYearId },
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "No records");
        setRows([]);
        return;
      }

      setRows(res.data.rows || []);
      toast.success(`Loaded ${(res.data.rows || []).length} record(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (empId, checked) => {
    setSelectedMap((prev) => ({ ...prev, [empId]: checked }));
  };

  const toggleAll = (checked) => {
    const next = {};
    rows.forEach((r) => {
      const id = getEmployeeId(r);
      if (id) next[id] = checked;
    });
    setSelectedMap(next);
  };

  const allChecked = useMemo(() => {
    if (rows.length === 0) return false;
    return rows.every((r) => selectedMap[getEmployeeId(r)]);
  }, [rows, selectedMap]);

  const updateEditField = (empId, field, value) => {
    setEditValues((prev) => ({
      ...prev,
      [empId]: { ...prev[empId], [field]: value },
    }));
  };

  const onSubmit = async () => {
    if (selectedIds.length === 0) return toast.error("Please select any one");

    const chosen = rows
      .filter((r) => selectedMap[getEmployeeId(r)])
      .map((r) => {
        const empId = getEmployeeId(r);
        const ev = editValues[empId] || {};
        return {
          employeeId: empId,
          designationId: getDesignationId(r),
          branchId: getBranchId(r),
          daysWorked: ev.daysWorked ?? r?.fld_Daysworked ?? 0,
          leave: ev.leave ?? r?.fld_Leave ?? 0,
          absence: ev.absence ?? r?.fld_Absence ?? 0,
          compulsoryLop: r?.fld_Compulsorylop ?? r?.fld_compulsorylop ?? 0,
          manageLeave: ev.manageLeave ?? r?.fld_ManageLeave ?? 0,
          holiday: ev.holiday ?? r?.fld_holiday ?? 0,
          lop: r?.fld_lopdays ?? r?.fld_lop ?? 0,
          ot: ev.ot ?? r?.fld_OT ?? 0,
          remarks: ev.remarks ?? r?.fld_Remarks ?? "",
          totLeaveOb: getLeaveOb(r),
          manageLeave100p: r?.fld_manageleave100p ?? 0,
          salaryDays: r?.salarydays ?? 0,
        };
      });

    try {
      setLoading(true);

      const payload = {
        isEditMode,
        maid: maid || null,
        monthVal: safeStr(monthVal),
        year: safeStr(year),
        rows: chosen,
      };

      const endpoint = isEditMode
        ? `/api/payroll/monthly-attendance/update`
        : `/api/payroll/monthly-attendance/submit`;

      const res = await axios.post(endpoint, payload, {
        ...axiosCfg,
        params: { finYearId },
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Submit failed");
        return;
      }

      toast.success(res.data?.message || (isEditMode ? "Updated Successfully" : "Inserted Successfully"));
      localStorage.removeItem("MAID");
      setTimeout(() => nav("/payroll/MonthlyAttendance"), 800);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Submit failed");
    } finally {
      setLoading(false);
    }
  };

  const onBack = () => {
    localStorage.removeItem("MAID");
    nav("/payroll/MonthlyAttendance");
  };

  const smallInput = "w-20 px-2 py-1 rounded border text-sm font-semibold text-black";

  return (
    <PayRollLayoutWrapper>
      <div className={`${pageBg} min-h-screen`}>
        <HeaderBar />
        <ToastContainer position="top-right" autoClose={2500} />

        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className={`${cardBase} p-6 mb-6`}>
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-2xl ${isDark ? "bg-white/10" : "bg-gray-100"}`}>
                  <Filter className={isDark ? "text-white" : "text-gray-800"} />
                </div>
                <div>
                  <div className="text-2xl font-extrabold leading-tight">
                    Monthly Attendance {isEditMode ? "(Edit)" : "(Entry)"}
                  </div>
                  <div className={`text-sm font-semibold ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                    INFRA SCREEN : C-23
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <button onClick={onBack} className={grayBtn} type="button">
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <button onClick={loadMeta} className={grayBtn} disabled={metaLoading || isEditMode} type="button">
                  <RefreshCw className="w-4 h-4" /> Reload Meta
                </button>
              </div>
            </div>
          </div>

          {!isEditMode && (
            <div className={`${cardBase} p-6 mb-6`}>
              <div className="flex items-center gap-2 mb-4">
                <Filter className={isDark ? "text-gray-200" : "text-gray-700"} />
                <div className="text-lg font-extrabold">Filters</div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-end">
                <div className="lg:col-span-4">
                  <label className={labelBase}>Branch <span className="text-red-500">*</span></label>
                  <select className={`${inputBase} w-full`} value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                    <option value="Select">Select</option>
                    {branches.map((b, idx) => {
                      const id = safeStr(pickBranchId(b));
                      const name = safeStr(pickBranchName(b));
                      if (!id) return null;
                      return <option key={`${id}-${idx}`} value={id}>{name || id}</option>;
                    })}
                  </select>
                </div>

                <div className="lg:col-span-2">
                  <label className={labelBase}>From Date</label>
                  <input type="date" className={`${inputBase} w-full`} value={fromDateISO} onChange={(e) => setFromDateISO(e.target.value)} />
                </div>

                <div className="lg:col-span-2">
                  <label className={labelBase}>To Date</label>
                  <input type="date" className={`${inputBase} w-full`} value={toDateISO} onChange={(e) => setToDateISO(e.target.value)} />
                </div>

                <div className="lg:col-span-2">
                  <label className={labelBase}>Year <span className="text-red-500">*</span></label>
                  <select className={`${inputBase} w-full`} value={year} onChange={(e) => setYear(e.target.value)}>
                    {years.map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>

                <div className="lg:col-span-2">
                  <label className={labelBase}>Month <span className="text-red-500">*</span></label>
                  <select className={`${inputBase} w-full`} value={monthVal} onChange={(e) => setMonthVal(e.target.value)}>
                    <option value="Select">Select</option>
                    {months.map((m) => <option key={m.val} value={m.val}>{m.name}</option>)}
                  </select>
                </div>

                <div className="lg:col-span-4">
                  <label className={labelBase}>Department</label>
                  <select className={`${inputBase} w-full`} value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
                    <option value="Select">Select</option>
                    {departments.map((d, idx) => {
                      const id = safeStr(pickDeptId(d));
                      const name = safeStr(pickDeptName(d));
                      if (!id) return null;
                      return <option key={`${id}-${idx}`} value={id}>{name || id}</option>;
                    })}
                  </select>
                </div>

                <div className="lg:col-span-8 lg:justify-self-end">
                  <button onClick={onSearch} className={primaryBtn} disabled={loading}>
                    <Search className="w-4 h-4" /> Search
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className={`${cardBase} p-0 overflow-hidden`}>
            <div className={`px-6 py-4 flex items-center justify-between ${isDark ? "border-b border-white/10" : "border-b border-gray-200"}`}>
              <div className="font-extrabold">{isEditMode ? "Edit Record" : "Employee List"}</div>
              <div className={`${isDark ? "text-gray-300" : "text-gray-600"} text-sm font-semibold`}>
                {rows.length} record(s) | Selected: {selectedIds.length}
              </div>
            </div>

            <div className="overflow-auto">
              <table className="min-w-[1400px] w-full">
                <thead className={`${isDark ? "bg-emerald-500/20" : "bg-emerald-600 text-white"}`}>
                  <tr>
                    <th className="px-4 py-4 text-left">S.No</th>
                    <th className="px-4 py-4 text-left">Employee Code</th>
                    <th className="px-4 py-4 text-left">Employee Name</th>
                    <th className="px-4 py-4 text-left">Designation</th>
                    <th className="px-4 py-4 text-left">Opening Leave</th>
                    <th className="px-4 py-4 text-left">Days Worked</th>
                    <th className="px-4 py-4 text-left">Leave</th>
                    <th className="px-4 py-4 text-left">Holiday</th>
                    <th className="px-4 py-4 text-left">Manage Leave</th>
                    <th className="px-4 py-4 text-left">LOP</th>
                    <th className="px-4 py-4 text-left">OT</th>
                    <th className="px-4 py-4 text-left">Remarks</th>
                    <th className="px-4 py-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <input type="checkbox" checked={allChecked} onChange={(e) => toggleAll(e.target.checked)} />
                        <span>Select All</span>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={13} className="px-6 py-10 text-center font-extrabold">No Records Found</td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => {
                      const empId = getEmployeeId(r);
                      const lop = Number(r?.fld_lopdays ?? r?.fld_lop ?? 0);
                      const isDanger = lop !== 0;
                      const ev = editValues[empId] || {};

                      return (
                        <tr
                          key={empId || idx}
                          className={
                            isDanger
                              ? "bg-red-600 text-white"
                              : isDark
                              ? "border-b border-white/10 hover:bg-white/5"
                              : "border-b border-gray-200 hover:bg-gray-50"
                          }
                        >
                          <td className="px-4 py-4 font-bold">{idx + 1}</td>
                          <td className="px-4 py-4 font-extrabold">
                            {r?.fld_EmployeeCode ?? r?.fld_Employeecode ?? r?.fld_employeecode ?? "-"}
                          </td>
                          <td className="px-4 py-4 font-bold">{r?.fld_Employee ?? r?.fld_employee ?? "-"}</td>
                          <td className="px-4 py-4 font-bold">{r?.fld_designationname ?? "-"}</td>
                          <td className="px-4 py-4 font-bold">{getLeaveOb(r) || "0"}</td>

                          {/* Days Worked */}
                          <td className="px-4 py-4">
                            {isEditMode ? (
                              <input
                                className={smallInput}
                                value={ev.daysWorked ?? ""}
                                onChange={(e) => updateEditField(empId, "daysWorked", e.target.value)}
                              />
                            ) : (
                              <span className="font-bold">{r?.fld_Daysworked ?? "0"}</span>
                            )}
                          </td>

                          {/* Leave */}
                          <td className="px-4 py-4">
                            {isEditMode ? (
                              <input
                                className={smallInput}
                                value={ev.leave ?? ""}
                                onChange={(e) => updateEditField(empId, "leave", e.target.value)}
                              />
                            ) : (
                              <span className="font-bold">{r?.fld_Leave ?? "0"}</span>
                            )}
                          </td>

                          {/* Holiday */}
                          <td className="px-4 py-4">
                            {isEditMode ? (
                              <input
                                className={smallInput}
                                value={ev.holiday ?? ""}
                                onChange={(e) => updateEditField(empId, "holiday", e.target.value)}
                              />
                            ) : (
                              <span className="font-bold">{r?.fld_holiday ?? "0"}</span>
                            )}
                          </td>

                          {/* Manage Leave */}
                          <td className="px-4 py-4">
                            {isEditMode ? (
                              <input
                                className={smallInput}
                                value={ev.manageLeave ?? ""}
                                onChange={(e) => updateEditField(empId, "manageLeave", e.target.value)}
                              />
                            ) : (
                              <span className="font-bold">{r?.fld_ManageLeave ?? "0"}</span>
                            )}
                          </td>

                          <td className="px-4 py-4 font-extrabold">{lop}</td>

                          {/* OT */}
                          <td className="px-4 py-4">
                            {isEditMode ? (
                              <input
                                className={smallInput}
                                value={ev.ot ?? ""}
                                onChange={(e) => updateEditField(empId, "ot", e.target.value)}
                              />
                            ) : (
                              <span className="font-bold">{r?.fld_OT ?? "0"}</span>
                            )}
                          </td>

                          {/* Remarks */}
                          <td className="px-4 py-4">
                            {isEditMode ? (
                              <input
                                className="w-40 px-2 py-1 rounded border text-sm font-semibold text-black"
                                value={ev.remarks ?? ""}
                                onChange={(e) => updateEditField(empId, "remarks", e.target.value)}
                              />
                            ) : (
                              <span className="font-semibold">{r?.fld_Remarks ?? ""}</span>
                            )}
                          </td>

                          <td className="px-4 py-4 text-center">
                            <input
                              type="checkbox"
                              checked={!!selectedMap[empId]}
                              onChange={(e) => toggleRow(empId, e.target.checked)}
                              disabled={!empId}
                            />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className={`px-6 py-4 flex justify-end gap-3 ${isDark ? "border-t border-white/10" : "border-t border-gray-200"}`}>
              <button onClick={onSubmit} className={successBtn} disabled={loading || selectedIds.length === 0}>
                <Save className="w-4 h-4" />
                {isEditMode ? "Update Selected" : "Submit Selected"}
              </button>
            </div>
          </div>

          <div className={`mt-4 text-sm ${isDark ? "text-gray-300" : "text-gray-600"} font-semibold`}>
            Note: Rows with LOP ≠ 0 are highlighted in red. Submit only processes checked employees.
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
}